/**
 * หน้าจอ "ป้าย Kanban" การผลิต — เลือกใบสั่งผลิต ดูป้ายของแต่ละ operation
 * พิมพ์/พิมพ์ซ้ำ และพรีวิว+พิมพ์ป้าย A6 แนวนอน (D-169, D-170)
 *
 * **การเลือกใบสั่งผลิตใช้ฟังก์ชันรายการที่มีอยู่แล้ว** (`fetchProductionOrders` ของ
 * `lib/productionOrders/client.ts`) ไม่สร้าง endpoint/client ใหม่สำหรับขั้นนี้ —
 * เลือกได้เฉพาะใบที่ `RELEASED`/`IN_PROCESS` (D-170 invariant) จึงยิงสองคำขอตามสถานะ
 * นั้นตรง ๆ แล้วรวมผล ไม่ใช่ดึงทุกสถานะมากรองทิ้งฝั่งหน้าจอ
 *
 * เมื่อเลือกใบแล้ว ดึง **รายละเอียดใบนั้นใบเดียว** (`fetchProductionOrder`) เพื่อได้
 * `operations` snapshot เต็ม — รายการ list ไม่รับประกันว่าจะมี snapshot นี้มาด้วย (ดู
 * หมายเหตุของ `ProductionOrder.operations` ใน `lib/productionOrders/types.ts`)
 *
 * พิมพ์/พิมพ์ซ้ำสำเร็จแล้ว **อัปเดตเฉพาะแถวนั้น** จาก response ไม่โหลดทั้งตารางใหม่
 * (D-170 invariant) — แถวอื่นจึงไม่ถูกแตะแม้ผู้ใช้จะกดพิมพ์หลายแถวติดกัน
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย การซ่อนปุ่มเป็นเพียง UX เซิร์ฟเวอร์เป็นผู้ตัดสินทุกคำขอเสมอ
 */

import { useEffect, useRef, useState } from 'react';

import { KanbanTagSheet } from './KanbanTagSheet';
import {
  hasCrossPlantScope,
  orderStatusLabel,
  orderStatusTone,
  selectablePlants,
} from './productionOrderView';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import { orDash } from '../products/productView';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  listOutputTags,
  printOutputTag,
  reprintOutputTag,
  type OutputTagFailure,
} from '../../lib/outputTags/client';
import {
  currentOutputTags,
  filterOrdersByOrderNo,
  hasUntaggedPackages,
  oldOutputTags,
  outputTagFailureMessage,
  outputTagsByOperationSeq,
  outputTagStatusLabel,
  outputTagStatusTone,
} from '../../lib/outputTags/outputTagView';
import type { OutputTag } from '../../lib/outputTags/types';
import {
  fetchProductionOrder,
  fetchProductionOrders,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import { PRODUCTION_ORDER_MODULE, type ProductionOrder } from '../../lib/productionOrders/types';

/**
 * ข้อความภาษาไทยของความล้มเหลวตอนดึง**รายการ/รายละเอียดใบสั่งผลิต** — คนละชนิดกับ
 * `OutputTagFailure` (เซิร์ฟเวอร์คนละ endpoint คนละชุดรหัสกฎ) จึงไม่ใช้ร่วมฟังก์ชัน
 * เดียวกัน แม้สถานะทั่วไป (unauthenticated/not-found/network-error ฯลฯ) จะซ้ำกัน
 */
function orderFetchFailureMessage(failure: ProductionOrderFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่';
    case 'forbidden':
      return 'คุณไม่มีสิทธิ์ดูใบสั่งผลิตในโรงงานนี้';
    case 'not-found':
      return 'ไม่พบใบสั่งผลิตนี้ — อาจถูกยกเลิกหรือย้ายโรงงานไปแล้ว';
    case 'server-error':
      return `เซิร์ฟเวอร์ผิดพลาด (${failure.httpStatus})`;
    case 'network-error':
      return `เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ (${failure.detail})`;
    case 'invalid-response':
      return 'เซิร์ฟเวอร์ตอบข้อมูลที่อ่านไม่ได้';
    case 'conflict':
    case 'rule-violation':
    case 'bad-request':
      return failure.error.message ?? 'โหลดใบสั่งผลิตไม่สำเร็จ';
    case 'cancelled':
      return '';
  }
}

export interface OutputTagScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type OrderListState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly orders: readonly ProductionOrder[] }
  | { readonly status: 'failed'; readonly message: string };

type OperationsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly order: ProductionOrder;
      readonly tagsByOperation: ReadonlyMap<number, readonly OutputTag[]>;
    }
  | { readonly status: 'failed'; readonly message: string };

const ALL_PLANTS_VALUE = '';

/** เรียงตามเลขที่ใบสั่งผลิต — ใบที่ไม่มีเลข (ไม่ควรเกิด) ตกไปอยู่ท้ายสุด */
function byOrderNo(a: ProductionOrder, b: ProductionOrder): number {
  return (a.order_no ?? '').localeCompare(b.order_no ?? '');
}

export function OutputTagScreen({ permissions, apiBaseUrl, fetchImpl }: OutputTagScreenProps) {
  const canCreate = can(permissions, PRODUCTION_ORDER_MODULE, 'CREATE');

  const plants = selectablePlants(permissions);
  const crossPlant = hasCrossPlantScope(permissions);
  const defaultPlantCode = plants[0] ?? ALL_PLANTS_VALUE;

  const [plantCode, setPlantCode] = useState(defaultPlantCode);
  const [orderNoQuery, setOrderNoQuery] = useState('');
  const [listState, setListState] = useState<OrderListState>({ status: 'idle' });
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [operationsState, setOperationsState] = useState<OperationsState | null>(null);
  const [rowErrors, setRowErrors] = useState<Readonly<Record<number, string>>>({});
  const [packageErrors, setPackageErrors] = useState<Readonly<Record<string, string>>>({});
  const [pendingSeqs, setPendingSeqs] = useState<ReadonlySet<number>>(new Set());
  const [previewTags, setPreviewTags] = useState<readonly OutputTag[] | null>(null);

  // อ่านค่าล่าสุดได้จากภายใน callback ที่ resolve ช้า (print/reprint) โดยไม่ต้อง
  // พึ่ง closure ของ render ที่ callback นั้นถูกสร้างขึ้น — ปัญหาเดียวกับที่
  // `active`/`AbortController` แก้ให้ `useEffect` ข้างบนแล้ว แต่ event handler ไม่มี
  // cleanup function ให้พึ่งแบบนั้น
  const selectedOrderIdRef = useRef(selectedOrderId);
  useEffect(() => {
    selectedOrderIdRef.current = selectedOrderId;
  }, [selectedOrderId]);

  const canQuery = plants.length > 0 || crossPlant;

  useEffect(() => {
    if (!canQuery) {
      return;
    }
    const controller = new AbortController();
    let active = true;
    setListState({ status: 'loading' });

    const plantFilter = plantCode === ALL_PLANTS_VALUE ? {} : { plant_code: plantCode };
    void Promise.all([
      fetchProductionOrders({ ...plantFilter, status: 'RELEASED' }, { apiBaseUrl, fetchImpl, signal: controller.signal }),
      fetchProductionOrders({ ...plantFilter, status: 'IN_PROCESS' }, { apiBaseUrl, fetchImpl, signal: controller.signal }),
    ]).then(([released, inProcess]) => {
      if (!active) {
        return;
      }
      if (released.status === 'cancelled' || inProcess.status === 'cancelled') {
        return;
      }
      if (released.status !== 'ready') {
        setListState({ status: 'failed', message: orderFetchFailureMessage(released) });
        return;
      }
      if (inProcess.status !== 'ready') {
        setListState({ status: 'failed', message: orderFetchFailureMessage(inProcess) });
        return;
      }
      setListState({
        status: 'ready',
        orders: [...released.page.items, ...inProcess.page.items].sort(byOrderNo),
      });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canQuery, plantCode, apiBaseUrl, fetchImpl]);

  useEffect(() => {
    if (selectedOrderId === null) {
      return;
    }
    const controller = new AbortController();
    let active = true;
    setOperationsState({ status: 'loading' });
    setRowErrors({});

    void Promise.all([
      fetchProductionOrder(selectedOrderId, { apiBaseUrl, fetchImpl, signal: controller.signal }),
      listOutputTags(selectedOrderId, { apiBaseUrl, fetchImpl, signal: controller.signal }),
    ]).then(([orderResult, tagsResult]) => {
      if (!active) {
        return;
      }
      if (orderResult.status === 'cancelled' || tagsResult.status === 'cancelled') {
        return;
      }
      if (orderResult.status !== 'ready') {
        setOperationsState({
          status: 'failed',
          message: orderFetchFailureMessage(orderResult),
        });
        return;
      }
      if (tagsResult.status !== 'ready') {
        setOperationsState({ status: 'failed', message: outputTagFailureMessage(tagsResult) });
        return;
      }
      setOperationsState({
        status: 'ready',
        order: orderResult.order,
        tagsByOperation: outputTagsByOperationSeq(tagsResult.page.items),
      });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedOrderId, apiBaseUrl, fetchImpl]);

  const withPending = async (seq: number, run: () => Promise<void>) => {
    setPendingSeqs((prev) => new Set(prev).add(seq));
    try {
      await run();
    } finally {
      setPendingSeqs((prev) => {
        const next = new Set(prev);
        next.delete(seq);
        return next;
      });
    }
  };

  const applyPrintResult = (seq: number, newTags: readonly OutputTag[]) => {
    setOperationsState((prev) => {
      if (prev === null || prev.status !== 'ready') {
        return prev;
      }
      const existing = prev.tagsByOperation.get(seq) ?? [];
      const next = new Map(prev.tagsByOperation).set(seq, [...existing, ...newTags]);
      return { ...prev, tagsByOperation: next };
    });
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[seq];
      return next;
    });
  };

  // พิมพ์ซ้ำตอบป้ายใหม่เท่านั้น — ป้ายเดิมที่ถูกแทนที่ (REPRINTED) ไม่ได้กลับมาด้วย จึง
  // ปรับสถานะป้ายเดิมในมือเป็น REPRINTED เอง เพื่อย้ายไปกลุ่ม "ป้ายเก่า" โดยไม่โหลด
  // ทั้งตารางใหม่ (D-170 invariant)
  const applyReprintResult = (oldTag: OutputTag, newTag: OutputTag) => {
    setOperationsState((prev) => {
      if (prev === null || prev.status !== 'ready') {
        return prev;
      }
      const seq = oldTag.operation_seq;
      const existing = prev.tagsByOperation.get(seq) ?? [];
      const updatedOld: OutputTag = {
        ...oldTag,
        status: 'REPRINTED',
        replaced_by_tag_code: newTag.tag_code,
      };
      const nextList = existing
        .map((t) => (t.tag_code === oldTag.tag_code ? updatedOld : t))
        .concat(newTag);
      const next = new Map(prev.tagsByOperation).set(seq, nextList);
      return { ...prev, tagsByOperation: next };
    });
    setPackageErrors((prev) => {
      const next = { ...prev };
      delete next[oldTag.tag_code];
      return next;
    });
  };

  const applyRowFailure = (seq: number, failure: OutputTagFailure) => {
    if (failure.status === 'cancelled') {
      return;
    }
    setRowErrors((prev) => ({ ...prev, [seq]: outputTagFailureMessage(failure) }));
  };

  const applyPackageFailure = (tagCode: string, failure: OutputTagFailure) => {
    if (failure.status === 'cancelled') {
      return;
    }
    setPackageErrors((prev) => ({ ...prev, [tagCode]: outputTagFailureMessage(failure) }));
  };

  const printRow = (seq: number) => {
    if (selectedOrderId === null) {
      return;
    }
    const orderIdAtClick = selectedOrderId;
    void withPending(seq, async () => {
      const result = await printOutputTag(
        { production_order_id: orderIdAtClick, operation_seq: seq },
        { apiBaseUrl, fetchImpl },
      );
      // ผู้ใช้อาจกด "เปลี่ยนใบสั่งผลิต" ก่อนคำตอบนี้มาถึง — ผลที่มาช้าของใบเก่าต้อง
      // ไม่ไปปนกับตารางของใบใหม่ที่กำลังแสดงอยู่
      if (selectedOrderIdRef.current !== orderIdAtClick) {
        return;
      }
      if (result.status === 'ready') {
        applyPrintResult(seq, result.page.items);
      } else {
        applyRowFailure(seq, result);
      }
    });
  };

  const reprintPackage = (tag: OutputTag) => {
    const orderIdAtClick = selectedOrderId;
    void withPending(tag.operation_seq, async () => {
      const result = await reprintOutputTag(tag.tag_code, { apiBaseUrl, fetchImpl });
      if (selectedOrderIdRef.current !== orderIdAtClick) {
        return;
      }
      if (result.status === 'ready') {
        applyReprintResult(tag, result.tag);
      } else {
        applyPackageFailure(tag.tag_code, result);
      }
    });
  };

  if (!canQuery) {
    return (
      <div className="cap-page">
        <div className="cap-notice" data-testid="output-tag-no-plant" role="status">
          <StatusBadge tone="warning">ไม่มีโรงงานที่คุณเห็น</StatusBadge>
          <h1 className="cap-notice__title">สิทธิ์ของคุณยังไม่ครอบคลุมโรงงานใดเลย</h1>
          <p className="cap-notice__text">ป้าย Kanban เป็นของใบสั่งผลิตซึ่งผูกกับโรงงาน ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์โรงงาน</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cap-page" data-testid="output-tag-screen">
      <div className="cap-page__head">
        <h1 className="cap-page__title">
          ป้าย Kanban{' '}
          <InfoHint
            label="เกี่ยวกับป้าย Kanban การผลิต"
            text="หนึ่งป้ายต่อหนึ่ง operation ของใบสั่งผลิตที่ปล่อยแล้ว — รหัสป้ายจองเลข lot ไว้ล่วงหน้า และข้อมูลที่พิมพ์จริงถูกเก็บไว้เพื่อให้พิมพ์ซ้ำได้เหมือนเดิมแม้ข้อมูลหลักจะเปลี่ยนไปแล้ว (D-169, D-170)"
          />
        </h1>
      </div>

      {selectedOrderId === null ? (
        <section className="cap-card" aria-label="เลือกใบสั่งผลิต">
          <div className="cap-filters">
            {plants.length > 0 ? (
              <label className="cap-field">
                <span className="cap-field__label">โรงงาน (plant_code)</span>
                <select
                  className="cap-input"
                  data-testid="output-tag-plant-select"
                  value={plantCode}
                  onChange={(event) => {
                    setPlantCode(event.target.value);
                  }}
                >
                  {crossPlant ? <option value={ALL_PLANTS_VALUE}>ทุกโรงงานในขอบเขตของคุณ</option> : null}
                  {plants.map((code) => (
                    <option key={code} value={code}>
                      โรงงาน {code}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="cap-field">
              <span className="cap-field__label">ค้นด้วยเลขใบสั่งผลิต</span>
              <input
                className="cap-input"
                type="search"
                data-testid="output-tag-order-search"
                value={orderNoQuery}
                onChange={(event) => {
                  setOrderNoQuery(event.target.value);
                }}
                placeholder="เช่น PO-2026-000123"
              />
            </label>
          </div>

          {listState.status === 'loading' ? (
            <p className="cap-notice__text" data-testid="output-tag-order-list-loading">
              กำลังโหลดรายการใบสั่งผลิต…
            </p>
          ) : null}

          {listState.status === 'failed' ? (
            <p className="cap-notice__text" role="alert" data-testid="output-tag-order-list-error">
              {listState.message}
            </p>
          ) : null}

          {listState.status === 'ready' ? (
            (() => {
              const filtered = filterOrdersByOrderNo(listState.orders, orderNoQuery);
              if (filtered.length === 0) {
                return (
                  <p className="cap-notice__text" data-testid="output-tag-order-list-empty">
                    ไม่พบใบสั่งผลิตที่ปล่อยแล้วตรงกับเงื่อนไขนี้
                  </p>
                );
              }
              return (
                <ul className="cap-list" data-testid="output-tag-order-list">
                  {filtered.map((order) => (
                    <li key={order.production_order_id}>
                      <button
                        type="button"
                        className="cap-button cap-button--ghost"
                        data-testid={`output-tag-order-${order.production_order_id}`}
                        onClick={() => {
                          setSelectedOrderId(order.production_order_id);
                        }}
                      >
                        {orDash(order.order_no)} — {orDash(order.product_code)}{' '}
                        <StatusBadge tone={orderStatusTone(order.status)}>
                          {orderStatusLabel(order.status)}
                        </StatusBadge>
                      </button>
                    </li>
                  ))}
                </ul>
              );
            })()
          ) : null}
        </section>
      ) : (
        <section className="cap-card cap-card--table" aria-label="operation ของใบสั่งผลิตที่เลือก">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              {operationsState?.status === 'ready' ? orDash(operationsState.order.order_no) : 'กำลังโหลด…'}
            </h2>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="output-tag-change-order"
              onClick={() => {
                setSelectedOrderId(null);
                setOperationsState(null);
                setPreviewTags(null);
              }}
            >
              เปลี่ยนใบสั่งผลิต
            </button>
          </div>

          {operationsState?.status === 'loading' || operationsState === null ? (
            <p className="cap-notice__text" data-testid="output-tag-operations-loading">
              กำลังโหลด operation…
            </p>
          ) : null}

          {operationsState?.status === 'failed' ? (
            <p className="cap-notice__text" role="alert" data-testid="output-tag-operations-error">
              {operationsState.message}
            </p>
          ) : null}

          {operationsState?.status === 'ready' ? (
            <div className="cap-table-wrap">
              <table className="cap-table">
                <caption className="cap-visually-hidden">
                  ป้าย Kanban ต่อ operation ของใบสั่งผลิต {orDash(operationsState.order.order_no)}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">ลำดับ</th>
                    <th scope="col">Work Center</th>
                    <th scope="col">แพ็คเกจ</th>
                    <th scope="col">การดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {operationsState.order.operations.map((operation) => {
                    const seq = operation.operation_seq;
                    const allTags = operationsState.status === 'ready'
                      ? operationsState.tagsByOperation.get(seq) ?? []
                      : [];
                    const current = currentOutputTags(allTags);
                    const old = oldOutputTags(allTags);
                    const pending = pendingSeqs.has(seq);
                    const rowError = rowErrors[seq];
                    const canPrintMore = hasUntaggedPackages(current);

                    return (
                      <tr key={seq} data-testid={`output-tag-row-${seq}`}>
                        <th scope="row" className="cap-table__row-head">
                          {seq}
                        </th>
                        <td className="cap-table__code">{orDash(operation.work_center_code)}</td>
                        <td>
                          {current.length === 0 ? (
                            <StatusBadge tone="neutral">ยังไม่พิมพ์</StatusBadge>
                          ) : (
                            <ul className="cap-list" data-testid={`output-tag-packages-${seq}`}>
                              {current.map((t) => {
                                const packageError = packageErrors[t.tag_code];
                                return (
                                  <li key={t.tag_code} data-testid={`output-tag-package-${seq}-${t.package_seq}`}>
                                    <span className="cap-table__code" data-testid={`output-tag-code-${seq}-${t.package_seq}`}>
                                      แพ็คเกจ {t.package_seq}/{t.package_count} · {orDash(t.tag_qty)} · {t.tag_code}
                                    </span>{' '}
                                    <span className="cap-table__code">{t.lot_no}</span>{' '}
                                    <StatusBadge tone={outputTagStatusTone(t.status)}>
                                      {outputTagStatusLabel(t.status)}
                                    </StatusBadge>
                                    <div className="cap-row-actions">
                                      <button
                                        type="button"
                                        className="cap-button cap-button--ghost"
                                        data-testid={`output-tag-preview-${seq}-${t.package_seq}`}
                                        disabled={pending}
                                        onClick={() => {
                                          // `t` มี `print_data` ครบตามที่ router ตอบทุก
                                          // endpoint (list/get/print/reprint) ไม่ต้องยิง
                                          // คำขอใหม่เพื่อดูสิ่งที่มีอยู่ในมืออยู่แล้ว
                                          setPreviewTags([t]);
                                        }}
                                      >
                                        พรีวิว/พิมพ์
                                      </button>
                                      {canCreate ? (
                                        <button
                                          type="button"
                                          className="cap-button cap-button--ghost"
                                          data-testid={`output-tag-reprint-${seq}-${t.package_seq}`}
                                          disabled={pending}
                                          onClick={() => {
                                            reprintPackage(t);
                                          }}
                                        >
                                          พิมพ์ซ้ำ
                                        </button>
                                      ) : null}
                                    </div>
                                    {packageError !== undefined ? (
                                      <p
                                        className="cap-notice__text"
                                        role="alert"
                                        data-testid={`output-tag-package-error-${seq}-${t.package_seq}`}
                                      >
                                        {packageError}
                                      </p>
                                    ) : null}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {old.length > 0 ? (
                            <details data-testid={`output-tag-old-tags-${seq}`} style={{ opacity: 0.6 }}>
                              <summary>ป้ายเก่า</summary>
                              <ul className="cap-list">
                                {old.map((t) => (
                                  <li key={t.tag_code} data-testid={`output-tag-old-tag-${t.tag_code}`}>
                                    แพ็คเกจ {t.package_seq}/{t.package_count} · {t.tag_code} ·{' '}
                                    <StatusBadge tone={outputTagStatusTone(t.status)}>
                                      {outputTagStatusLabel(t.status)}
                                    </StatusBadge>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          ) : null}
                        </td>
                        <td>
                          <div className="cap-row-actions">
                            {canCreate && canPrintMore ? (
                              <button
                                type="button"
                                className="cap-button cap-button--primary"
                                data-testid={`output-tag-print-${seq}`}
                                disabled={pending}
                                onClick={() => {
                                  printRow(seq);
                                }}
                              >
                                พิมพ์ป้าย
                              </button>
                            ) : null}
                            {current.length > 0 ? (
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid={`output-tag-print-all-${seq}`}
                                disabled={pending}
                                onClick={() => {
                                  setPreviewTags(current);
                                }}
                              >
                                พิมพ์ทั้งหมดของ operation
                              </button>
                            ) : null}
                          </div>
                          {rowError !== undefined ? (
                            <p
                              className="cap-notice__text"
                              role="alert"
                              data-testid={`output-tag-row-error-${seq}`}
                            >
                              {rowError}
                            </p>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      )}

      {previewTags !== null ? (
        <KanbanTagSheet
          tag={previewTags}
          onClose={() => {
            setPreviewTags(null);
          }}
        />
      ) : null}
    </div>
  );
}
